/**
 * Kraje dostępne w wyborze prefiksu telefonu (patrz phone-country.js).
 *
 * Każdy wpis to: [kod ISO 3166-1 alpha-2, nazwa po polsku, prefiks, min. liczba
 * cyfr numeru krajowego, maks. liczba cyfr]. Maks. pomijamy, gdy długość jest
 * stała — wtedy maska wymaga dokładnie `min` cyfr, a przy zakresie nadwyżkę
 * cyfr robi opcjonalną (`[0]` w składni IMask).
 *
 * Flagi leżą w assets/flags/<kod>.svg (flag-icons, MIT — patrz assets/flags/LICENSE),
 * więc dodanie kraju to nowy wpis tutaj plus plik SVG o tej samej nazwie.
 */
const COUNTRY_DATA = [
	['al', 'Albania', '+355', 8, 9],
	['ad', 'Andora', '+376', 6],
	['au', 'Australia', '+61', 9],
	['at', 'Austria', '+43', 6, 13],
	['be', 'Belgia', '+32', 8, 9],
	['by', 'Białoruś', '+375', 9],
	['ba', 'Bośnia i Hercegowina', '+387', 8],
	['bg', 'Bułgaria', '+359', 8, 9],
	['hr', 'Chorwacja', '+385', 8, 9],
	['cy', 'Cypr', '+357', 8],
	['me', 'Czarnogóra', '+382', 8],
	['cz', 'Czechy', '+420', 9],
	['dk', 'Dania', '+45', 8],
	['ee', 'Estonia', '+372', 7, 8],
	['fi', 'Finlandia', '+358', 6, 10],
	['fr', 'Francja', '+33', 9],
	['gr', 'Grecja', '+30', 10],
	['ge', 'Gruzja', '+995', 9],
	['es', 'Hiszpania', '+34', 9],
	['nl', 'Holandia', '+31', 9],
	['ie', 'Irlandia', '+353', 7, 9],
	['is', 'Islandia', '+354', 7],
	['ca', 'Kanada', '+1', 10],
	['xk', 'Kosowo', '+383', 8, 9],
	['li', 'Liechtenstein', '+423', 7],
	['lt', 'Litwa', '+370', 8],
	['lu', 'Luksemburg', '+352', 6, 9],
	['lv', 'Łotwa', '+371', 8],
	['mk', 'Macedonia Północna', '+389', 8],
	['mt', 'Malta', '+356', 8],
	['md', 'Mołdawia', '+373', 8],
	['mc', 'Monako', '+377', 8, 9],
	['de', 'Niemcy', '+49', 6, 11],
	['no', 'Norwegia', '+47', 8],
	['pl', 'Polska', '+48', 9],
	['pt', 'Portugalia', '+351', 9],
	['ru', 'Rosja', '+7', 10],
	['ro', 'Rumunia', '+40', 9],
	['sm', 'San Marino', '+378', 6, 10],
	['rs', 'Serbia', '+381', 8, 9],
	['sk', 'Słowacja', '+421', 9],
	['si', 'Słowenia', '+386', 8],
	['us', 'Stany Zjednoczone', '+1', 10],
	['ch', 'Szwajcaria', '+41', 9],
	['se', 'Szwecja', '+46', 7, 9],
	['tr', 'Turcja', '+90', 10],
	['ua', 'Ukraina', '+380', 9],
	['hu', 'Węgry', '+36', 8, 9],
	['gb', 'Wielka Brytania', '+44', 9, 10],
	['it', 'Włochy', '+39', 6, 11],
];

// Webpack podstawia zahaszowane adresy plików z assets/flags — dzięki temu lista
// krajów nie musi znać ścieżek publicznych ani manifestu Encore.
const flagUrls = require.context('../../flags', false, /\.svg$/);

/**
 * @typedef {object} PhoneCountry
 * @property {string} iso - kod ISO 3166-1 alpha-2, np. `pl`
 * @property {string} name - nazwa kraju po polsku
 * @property {string} prefix - prefiks międzynarodowy z plusem, np. `+48`
 * @property {number} minDigits - najkrótszy dopuszczalny numer krajowy
 * @property {number} maxDigits - najdłuższy dopuszczalny numer krajowy
 * @property {string} label - podpis na liście, np. `Polska (+48)`
 * @property {string} mask - maska IMask samego numeru krajowego, bez prefiksu
 * @property {string} flag - adres pliku SVG z flagą
 * @property {string} search - nazwa i prefiks w formie do porównań w wyszukiwarce
 */

/** @type {PhoneCountry[]} */
export const COUNTRIES = COUNTRY_DATA
	.map(([iso, name, prefix, minDigits, maxDigits = minDigits]) => ({
		iso,
		name,
		prefix,
		minDigits,
		maxDigits,
		label: `${name} (${prefix})`,
		// Maska obejmuje tylko numer krajowy — prefiksu w niej nie ma, bo w IMask
		// znak stały „zjada" wpisaną, taką samą cyfrę (przy masce `{+48} …` numer
		// z Radomia, 48 333 12 34, tracił pierwszą czwórkę). Prefiks rysujemy więc
		// obok pola, a pełny numer składa phone-country.js.
		// Cyfry ponad minimum są opcjonalne, więc maska uznaje numer za kompletny
		// już przy najkrótszym poprawnym numerze, ale pozwala dopisać dłuższy.
		mask: `${'0'.repeat(minDigits)}${maxDigits > minDigits ? `[${'0'.repeat(maxDigits - minDigits)}]` : ''}`,
		flag: flagUrls(`./${iso}.svg`),
		search: normalize(`${name} ${prefix}`),
	}))
	.sort((a, b) => a.name.localeCompare(b.name, 'pl'));

/** Kraj wybrany, dopóki użytkownik nie wskaże innego. */
export const DEFAULT_COUNTRY = findByIso('pl') ?? COUNTRIES[0];

/**
 * Ujednolica tekst do porównań: bez wielkości liter i bez polskich ogonków,
 * żeby „lotwa" znalazło „Łotwę", a „bosnia" — „Bośnię i Hercegowinę".
 *
 * @param {string} text
 * @returns {string}
 */
export function normalize(text) {
	return text
		.toLowerCase()
		.normalize('NFD')
		.replace(/[\u0300-\u036f]/g, '')
		.replace(/ł/g, 'l');
}

/**
 * @param {string} iso
 * @returns {PhoneCountry|undefined}
 */
export function findByIso(iso) {
	return COUNTRIES.find(country => country.iso === iso);
}

/**
 * Rozpoznaje kraj po numerze wpisanym w pole — po najdłuższym pasującym
 * prefiksie, bo krótsze prefiksy (np. `+1`) są początkiem dłuższych.
 *
 * @param {string} value - treść pola, np. `+380 123456789`
 * @returns {PhoneCountry|undefined}
 */
export function findByNumber(value) {
	const digits = String(value ?? '').replace(/[^\d+]/g, '');
	if (!digits.startsWith('+')) {
		return undefined;
	}

	return COUNTRIES
		.filter(country => digits.startsWith(country.prefix))
		.sort((a, b) => b.prefix.length - a.prefix.length)[0];
}

/**
 * Filtruje listę po nazwie kraju albo po prefiksie. Pusta fraza zwraca całość.
 *
 * @param {string} phrase
 * @returns {PhoneCountry[]}
 */
export function search(phrase) {
	const needle = normalize(phrase).trim().replace(/^00/, '+');
	if (!needle) {
		return COUNTRIES;
	}

	return COUNTRIES.filter(country => country.search.includes(needle));
}

/**
 * Podpowiedź o formacie numeru wybranego kraju, np. „w formacie 9 cyfr bez
 * spacji" albo „w formacie od 6 do 11 cyfr bez spacji". Prefiksu w niej nie ma,
 * bo stoi jako osobny napis przed treścią pola.
 *
 * @param {PhoneCountry} country
 * @returns {string}
 */
export function placeholderFor(country) {
	const { minDigits, maxDigits } = country;
	const format = minDigits === maxDigits
		? `${minDigits} ${digitsNoun(minDigits)}`
		: `od ${minDigits} do ${maxDigits} ${digitsNoun(maxDigits)}`;

	return `w formacie ${format} bez spacji`;
}

/**
 * Odmiana słowa „cyfra" przez liczebnik: 1 cyfra, 2–4 cyfry, 5+ cyfr
 * (z wyjątkiem nastek: 12 cyfr, 13 cyfr).
 *
 * @param {number} count
 * @returns {string}
 */
function digitsNoun(count) {
	if (count === 1) {
		return 'cyfra';
	}

	const teens = count % 100 >= 12 && count % 100 <= 14;

	return !teens && count % 10 >= 2 && count % 10 <= 4 ? 'cyfry' : 'cyfr';
}
